/* The ledger's rows, and everything a reader filters or sorts them by. Pure — no React, no fetch.
 *
 * Here rather than in the component for the reason `ledgerRows` already gives: the ordering has to
 * be testable, and the screen has to be able to pick a default selection from the *same* order the
 * table renders in. Two orderings put the highlight on a different row from the one the drawer
 * describes, which is the two-vocabularies failure at its most confusing.
 *
 * Everything below is derived. Nothing here holds a threshold that also exists somewhere else:
 * `GOOD_SCORE` and `POOR_SCORE` come from `presentation.ts`, which mirrors `ScoreBar`, so a bar, a
 * badge and a saved view cannot disagree about what a good score looks like.
 */

import type { ScoreSummary } from '../api/scoring';

import { scoreBand, unscoredReading } from './presentation';
import type { ProfileFacts } from './presentation';
import { slackOf } from './targets';
import type { DatasetRow, Movement, QualityTarget, ScoreStatus } from './types';

/** How far below a target counts as critical rather than merely short.
 *
 * A tenth of a point under a floor is a breach and needs saying; it is not the same emergency as
 * four points under. Without a second band every near-miss would wear the same red as a collapse,
 * and a reader who sees ten reds learns to read none of them.
 */
export const CRITICAL_BREACH_POINTS = 3;

/** After this long without a re-evaluation, a score describes a dataset as it was rather than as it
 * is. Stale is reported as its own state and never folded into the score: an old 94 is not a bad
 * score, it is a score somebody should stop relying on. */
export const STALE_AFTER_HOURS = 24;

const MS_PER_HOUR = 3_600_000;

/** Hours since an instant, or `null` for one that will not parse.
 *
 * `null` rather than a zero age, for `stalenessAge`'s reason: an unparseable timestamp means we do
 * not know how old the figure is, and "0 hours ago" is the most reassuring thing this screen could
 * say about exactly that. A *future* instant reads as 0 rather than negative — clock skew between a
 * browser and a server is real, and "evaluated in the future" is not staleness.
 */
export function hoursSince(instant: string | null, now: number): number | null {
  if (instant === null) return null;
  const at = new Date(instant).getTime();
  if (Number.isNaN(at)) return null;
  return Math.max(0, (now - at) / MS_PER_HOUR);
}

/**
 * The verdict for one row, from its score and its target together.
 *
 * **A met target is never reported as critical**, even when the score is low by the platform's own
 * bands. A tenant who signed for ≥ 60 and scores 62 is meeting the promise they made, and a screen
 * that called that critical would be arguing with a contract two parties agreed. It does not read
 * as healthy either — a met target under the poor band is still a warning, because the promise
 * being weak is itself worth seeing.
 *
 * With no target, the platform's bands are the only verdict available and are used as such.
 */
export function statusOf(value: number | null, slack: number | null): ScoreStatus {
  if (value === null) return 'unscored';
  const band = scoreBand(value);
  if (slack !== null) {
    if (slack < -CRITICAL_BREACH_POINTS) return 'critical';
    if (slack < 0) return 'warning';
    return band === 'danger' ? 'warning' : 'healthy';
  }
  return band === 'danger' ? 'critical' : band === 'warn' ? 'warning' : 'healthy';
}

export interface BuildRowsInput {
  readonly names: ReadonlyMap<string, string>;
  readonly scores: ReadonlyMap<string, ScoreSummary> | undefined;
  readonly facts: ReadonlyMap<string, ProfileFacts> | undefined;
  readonly targets: ReadonlyMap<string, QualityTarget>;
  readonly movements: ReadonlyMap<string, Movement>;
  readonly now: number;
}

/**
 * Every catalogued dataset as a row, in the ledger's canonical order.
 *
 * **Unscored first, always.** A ranked list is only useful if the top of it is where the work is,
 * and an unscored dataset is a gap in coverage rather than a zero. A profile *baseline* does not
 * move the row either: a baseline of 90 is evidence that nobody has written a rule yet, not
 * evidence of quality.
 */
export function buildRows(input: BuildRowsInput): readonly DatasetRow[] {
  const { names, scores, facts, targets, movements, now } = input;

  return [...names.entries()]
    .map(([id, name]): DatasetRow => {
      const summary = scores?.get(id);
      const fact = facts?.get(id);
      const value = summary?.value ?? null;
      const target = targets.get(id) ?? null;
      const slack = slackOf(value, target);
      const evaluatedAt = summary?.computed_at ?? null;
      const hours = hoursSince(evaluatedAt, now);
      /* Split on the first dot only. A schema name cannot contain one, an object name can, and
         `split('.')` would drop everything after the second dot from a quoted object name. */
      const dot = name.indexOf('.');
      return {
        id,
        name,
        schema: dot < 0 ? '' : name.slice(0, dot),
        object: dot < 0 ? name : name.slice(dot + 1),
        summary,
        unscored:
          value === null
            ? unscoredReading(
                fact?.profile_baseline ?? null,
                fact?.last_profiled_at ?? null,
              )
            : null,
        value,
        coverageRatio: summary?.coverage_ratio ?? null,
        target,
        slack,
        status: statusOf(value, slack),
        evaluatedAt,
        evaluatedHoursAgo: hours,
        /* Only a *scored* dataset can be stale. A dataset nobody has ever scored is unscored, and
           reporting it as stale as well would file one gap under two headings. */
        stale: value !== null && hours !== null && hours > STALE_AFTER_HOURS,
        evidenceComplete: summary?.evidence_complete ?? true,
        movement: movements.get(id),
      };
    })
    .sort(byStatusThenScore);
}

function byStatusThenScore(left: DatasetRow, right: DatasetRow): number {
  if (left.value === null && right.value === null)
    return left.name.localeCompare(right.name);
  if (left.value === null) return -1;
  if (right.value === null) return 1;
  if (left.value !== right.value) return left.value - right.value;
  return left.name.localeCompare(right.name);
}

/* ─────────────────────────────────────────────────────────────── saved views

 * A view is a named filter, and the built-in set is the vocabulary an enterprise reader already
 * has: the ones needing attention, the ones in breach, the ones that moved, the ones nobody has
 * measured, the ones whose measurement has gone stale.
 *
 * `predicate` is a plain function of a row, so a view saved on a server later is the same shape
 * with its predicate rebuilt from stored criteria. Nothing here reaches for a hook or a fetch.
 */
export type BuiltInViewId =
  | 'all'
  | 'attention'
  | 'critical'
  | 'below-target'
  | 'degraded'
  | 'unscored'
  | 'stale'
  | 'watch'
  | 'healthy';

export interface SavedView {
  readonly id: string;
  /** i18n key for a built-in. A view a reader saved carries `label` instead. */
  readonly labelKey?: string | undefined;
  readonly label?: string | undefined;
  readonly predicate: (row: DatasetRow) => boolean;
  /** Built-ins cannot be deleted; a reader's own can. */
  readonly builtIn: boolean;
  /** `false` for a view reached only from the band bar, so the toolbar keeps its own vocabulary:
   * "Needs attention" already covers both unhealthy bands, and two chips meaning nearly the same
   * thing side by side is how a filter row becomes something nobody reads. */
  readonly chip?: boolean | undefined;
}

/** A dataset that moved down by at least this much is "recently degraded". Deliberately lower than
 * the regression threshold: this view is for browsing what slipped, where a regression is an alert
 * somebody is expected to act on. */
export const DEGRADED_POINTS = 1;

export const BUILT_IN_VIEWS: readonly SavedView[] = [
  { id: 'all', labelKey: 'scoring.view.all', predicate: () => true, builtIn: true },
  {
    id: 'attention',
    labelKey: 'scoring.view.attention',
    predicate: (row) => row.status === 'critical' || row.status === 'warning',
    builtIn: true,
  },
  {
    id: 'critical',
    labelKey: 'scoring.view.critical',
    predicate: (row) => row.status === 'critical',
    builtIn: true,
  },
  {
    id: 'below-target',
    labelKey: 'scoring.view.belowTarget',
    predicate: (row) => row.slack !== null && row.slack < 0,
    builtIn: true,
  },
  {
    id: 'degraded',
    labelKey: 'scoring.view.degraded',
    predicate: (row) =>
      row.movement?.delta.kind === 'down' &&
      row.movement.delta.magnitude >= DEGRADED_POINTS,
    builtIn: true,
  },
  {
    id: 'unscored',
    labelKey: 'scoring.view.unscored',
    predicate: (row) => row.value === null,
    builtIn: true,
  },
  {
    id: 'stale',
    labelKey: 'scoring.view.stale',
    predicate: (row) => row.stale,
    builtIn: true,
  },
  /* The two bands the bar filters to that no chip names: Watch alone, and Healthy. */
  {
    id: 'watch',
    labelKey: 'scoring.band.watch',
    predicate: (row) => row.status === 'warning',
    builtIn: true,
    chip: false,
  },
  {
    id: 'healthy',
    labelKey: 'scoring.band.healthy',
    predicate: (row) => row.status === 'healthy',
    builtIn: true,
    chip: false,
  },
];

/* ─────────────────────────────────────────────────────────────── query */

export type SortKey =
  'status' | 'name' | 'score' | 'delta' | 'coverage' | 'target' | 'evaluated' | 'owner';

export interface TableQuery {
  readonly view: string;
  readonly search: string;
  /** `''` means every schema. A real grouping, unlike an environment, which this platform does not
   * model — the schema is on every qualified name and needs no extra request. */
  readonly schema: string;
  readonly sort: SortKey;
  readonly direction: 1 | -1;
}

export const DEFAULT_QUERY: TableQuery = {
  view: 'all',
  search: '',
  schema: '',
  sort: 'status',
  direction: 1,
};

/** True when the query differs from the default, i.e. when a Reset control has anything to do. */
export function queryIsFiltered(query: TableQuery): boolean {
  return query.view !== 'all' || query.search.trim() !== '' || query.schema !== '';
}

/** Every distinct schema in the estate, sorted, for the schema selector. */
export function schemasOf(rows: readonly DatasetRow[]): readonly string[] {
  return [...new Set(rows.map((row) => row.schema).filter((s) => s !== ''))].sort(
    (a, b) => a.localeCompare(b),
  );
}

/** Movement as a signed number for sorting: down is negative, up is positive, and every
 * non-numeric outcome is zero. Zero rather than a sentinel because "did not move", "no comparison"
 * and "not comparable" are all *not a movement*, and ranking them apart would order rows by which
 * kind of nothing they have. */
function movementRank(row: DatasetRow): number {
  const delta = row.movement?.delta;
  if (delta === undefined) return 0;
  if (delta.kind === 'down') return -delta.magnitude;
  if (delta.kind === 'up') return delta.magnitude;
  return 0;
}

/**
 * Where a row with **no value in the sorted column** belongs, independent of direction.
 *
 * This is the rule a first version got wrong and a test caught. `compare(...) * direction` reverses
 * everything it returns, absences included — so a listing that put unscored rows first when sorted
 * ascending buried them at the bottom the moment somebody sorted descending, which is precisely the
 * gesture a reader makes to find the best-scoring datasets. An estate with a third of it unmeasured
 * then reads as fully measured.
 *
 * So an absence is placed by what it *means* rather than by where the arithmetic lands it:
 *
 * * `top` — the work is here. No score, no movement read and no evaluation are all "nobody has
 *   measured this", and that is the first thing a quality screen owes a reader.
 * * `bottom` — nothing to report. No target promised and no owner recorded are absences of a
 *   *commitment*, not of a measurement; a row with nothing to breach is not the smallest breach.
 */
type AbsenceEnd = 'top' | 'bottom';

const ABSENCE_END: Partial<Record<SortKey, AbsenceEnd>> = {
  score: 'top',
  delta: 'top',
  evaluated: 'top',
  coverage: 'top',
  target: 'bottom',
  owner: 'bottom',
};

/** Whether a row has nothing to offer the column being sorted. */
function absentFor(row: DatasetRow, key: SortKey): boolean {
  switch (key) {
    case 'score':
      return row.value === null;
    case 'delta':
      return row.movement === undefined;
    case 'evaluated':
      return row.evaluatedAt === null;
    case 'coverage':
      return row.coverageRatio === null;
    case 'target':
      return row.slack === null;
    case 'owner':
      return row.target === null;
    default:
      return false;
  }
}

/** Filter, search and sort in one pass, so a component never holds three intermediate arrays. */
export function applyQuery(
  rows: readonly DatasetRow[],
  query: TableQuery,
  views: readonly SavedView[],
): readonly DatasetRow[] {
  const view = views.find((candidate) => candidate.id === query.view);
  const needle = query.search.trim().toLowerCase();

  const kept = rows.filter((row) => {
    if (view !== undefined && !view.predicate(row)) return false;
    if (query.schema !== '' && row.schema !== query.schema) return false;
    if (needle === '') return true;
    return (
      row.name.toLowerCase().includes(needle) ||
      (row.target?.owner.toLowerCase().includes(needle) ?? false)
    );
  });

  const end = ABSENCE_END[query.sort];

  return [...kept].sort((left, right) => {
    if (end !== undefined) {
      const leftAbsent = absentFor(left, query.sort);
      const rightAbsent = absentFor(right, query.sort);
      /* Two absences fall back to the name, so the group itself has a stable order rather than
         whatever `Array.sort` happened to do with a run of zeroes. */
      if (leftAbsent && rightAbsent) return left.name.localeCompare(right.name);
      if (leftAbsent) return end === 'top' ? -1 : 1;
      if (rightAbsent) return end === 'top' ? 1 : -1;
    }
    return compare(left, right, query.sort) * query.direction;
  });
}

const STATUS_RANK: Readonly<Record<ScoreStatus, number>> = {
  unscored: 0,
  critical: 1,
  warning: 2,
  healthy: 3,
};

/** Compares two rows that both have a value in the sorted column — absences never reach here. */
function compare(left: DatasetRow, right: DatasetRow, key: SortKey): number {
  const byName = left.name.localeCompare(right.name);
  switch (key) {
    case 'name':
      return byName;
    case 'score':
      return (left.value ?? 0) - (right.value ?? 0) || byName;
    case 'delta':
      return movementRank(left) - movementRank(right) || byName;
    case 'coverage':
      return (left.coverageRatio ?? 0) - (right.coverageRatio ?? 0) || byName;
    case 'target':
      return (left.slack ?? 0) - (right.slack ?? 0) || byName;
    case 'evaluated':
      /* Oldest first when ascending: the column a reader sorts to find what has stopped being
         re-measured, and the rows that have never been measured at all sit above it. */
      return (right.evaluatedHoursAgo ?? 0) - (left.evaluatedHoursAgo ?? 0) || byName;
    case 'owner':
      return (
        (left.target?.owner ?? '').localeCompare(right.target?.owner ?? '') || byName
      );
    case 'status':
    default:
      return (
        STATUS_RANK[left.status] - STATUS_RANK[right.status] ||
        (left.value ?? -1) - (right.value ?? -1) ||
        byName
      );
  }
}

/** How many rows fall in each status, for the summary strip and the view counts. */
export function countByStatus(
  rows: readonly DatasetRow[],
): Readonly<Record<ScoreStatus, number>> {
  const counts: Record<ScoreStatus, number> = {
    healthy: 0,
    warning: 0,
    critical: 0,
    unscored: 0,
  };
  for (const row of rows) counts[row.status] += 1;
  return counts;
}
